import { useEffect, useState } from "react";
import api from "@/lib/api";
import { money } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, X, Pencil, Trash2, ExternalLink, Loader2, Heart } from "lucide-react";

const inputCls =
  "w-full bg-ink border border-stroke px-3 py-2 font-mono text-sm text-paper placeholder:text-muted-gray focus:outline-none focus:border-acid";
const empty = { title: "", band: "", poster_artist: "", target_price: "", seller_link: "", priority: "medium", notes: "", image_url: "" };
const PRIORITIES = [
  { value: "grail", label: "Grail", cls: "border-gold/50 bg-gold/10 text-gold" },
  { value: "high", label: "High", cls: "border-magenta/50 bg-magenta/10 text-magenta" },
  { value: "medium", label: "Medium", cls: "border-cyan/50 bg-cyan/10 text-cyan" },
  { value: "low", label: "Low", cls: "border-stroke bg-elevated text-muted-gray" },
];
const prioCls = (p) => PRIORITIES.find((x) => x.value === p)?.cls || PRIORITIES[2].cls;

export default function WishlistPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const fetchItems = () => api.get("/wishlist").then((r) => setItems(r.data));
  useEffect(() => { fetchItems(); }, []);

  const openAdd = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (w) => { setEditing(w); setForm({ ...w, target_price: w.target_price ?? "" }); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    const payload = { ...form, target_price: form.target_price === "" ? null : Number(form.target_price) };
    try {
      if (editing) await api.put(`/wishlist/${editing.id}`, payload);
      else await api.post("/wishlist", payload);
      toast.success("Saved to grails");
      setOpen(false);
      fetchItems();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (w) => {
    if (!window.confirm(`Remove ${w.title} from grails?`)) return;
    await api.delete(`/wishlist/${w.id}`);
    toast.success("Removed");
    fetchItems();
  };

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-acid">Most Wanted</div>
          <h1 className="font-display text-5xl lg:text-6xl text-paper leading-none mt-1">GRAILS</h1>
          <div className="font-mono text-xs text-muted-gray mt-2">{items.length} on the hunt</div>
        </div>
        <button onClick={openAdd} data-testid="add-wishlist-button"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider bg-acid text-ink font-bold px-5 py-3 hard-shadow">
          <Plus size={16} /> Add Grail
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-stroke p-12 text-center font-mono text-sm text-muted-gray">
          Track the posters you're hunting — target price, seller links & priority.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((w) => (
            <div key={w.id} className="bg-surface border border-stroke overflow-hidden hard-shadow-magenta hard-shadow group" data-testid="wishlist-card">
              {w.image_url && (
                <div className="aspect-[3/4] bg-ink overflow-hidden">
                  <img src={w.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <span className={`font-mono text-[10px] uppercase px-2 py-0.5 border ${prioCls(w.priority)}`}>{w.priority}</span>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openEdit(w)} className="text-muted-gray hover:text-acid"><Pencil size={14} /></button>
                    <button onClick={() => del(w)} className="text-muted-gray hover:text-magenta"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-acid mt-3">{w.band || "—"}</div>
                <div className="font-display text-2xl text-paper leading-tight">{w.title}</div>
                {w.poster_artist && <div className="font-mono text-[11px] text-muted-gray">art · {w.poster_artist}</div>}
                {w.notes && <p className="font-sans text-xs text-muted-gray mt-2 line-clamp-2">{w.notes}</p>}
                <div className="flex items-center justify-between mt-3">
                  <span className="font-mono text-sm text-gold">{w.target_price != null ? `≤ ${money(w.target_price)}` : ""}</span>
                  {w.seller_link && (
                    <a href={w.seller_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-[11px] text-cyan hover:text-acid">
                      <ExternalLink size={12} /> Seller
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/85 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg bg-surface border border-stroke my-8" data-testid="wishlist-form-dialog">
            <div className="flex items-center justify-between p-5 border-b border-stroke">
              <h2 className="font-display text-3xl text-paper flex items-center gap-2"><Heart className="text-magenta" size={22} />{editing ? "Edit Grail" : "Add Grail"}</h2>
              <button onClick={() => setOpen(false)} className="text-muted-gray hover:text-magenta"><X size={22} /></button>
            </div>
            <div className="p-5 space-y-4">
              <input data-testid="wishlist-title-input" className={inputCls} placeholder="Poster title *"
                value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <input className={inputCls} placeholder="Band" value={form.band} onChange={(e) => setForm({ ...form, band: e.target.value })} />
                <input className={inputCls} placeholder="Poster artist" value={form.poster_artist} onChange={(e) => setForm({ ...form, poster_artist: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input type="number" className={inputCls} placeholder="Target price ($)" value={form.target_price} onChange={(e) => setForm({ ...form, target_price: e.target.value })} />
                <select className={inputCls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                  {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <input className={inputCls} placeholder="Seller link (URL)" value={form.seller_link} onChange={(e) => setForm({ ...form, seller_link: e.target.value })} />
              <input className={inputCls} placeholder="Image URL (optional)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
              <textarea className={`${inputCls} min-h-[70px] resize-y`} placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="p-5 border-t border-stroke flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="px-5 py-2.5 font-mono text-xs uppercase border border-stroke text-muted-gray hover:text-paper">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="wishlist-save-button"
                className="px-6 py-2.5 font-mono text-xs uppercase bg-acid text-ink font-bold hover:bg-glow flex items-center gap-2 disabled:opacity-50">
                {saving && <Loader2 size={14} className="animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
